import { useCallback, useEffect, useRef, useState } from 'react';

import type { Locale } from '@/lib/i18n';

/* ---------------------------------------------------------------------------
   זיהוי דיבור של הדפדפן (Web Speech API) — לשלב ההרשמה בלבד, כשעוד אין עסק
   ואין תמלול בשרת. Chrome ו-Safari תומכים; בדפדפן בלי תמיכה הכפתור לא מוצג.
   אחרי ההרשמה ההקלטה עוברת לעוזר (תמלול בשרת, Composer).
   --------------------------------------------------------------------------- */

interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}
type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useSpeechInput(locale: Locale, onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const rec = useRef<Recognition | null>(null);
  const handler = useRef(onText);
  handler.current = onText;
  const Ctor = recognitionCtor();

  useEffect(() => () => rec.current?.stop(), []);

  const start = useCallback(() => {
    if (!Ctor) return;
    const r = new Ctor();
    r.lang = locale === 'he' ? 'he-IL' : 'en-US';
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (e) => {
      const text = Array.from(e.results)
        .map((res) => res[0]?.transcript ?? '')
        .join(' ')
        .trim();
      if (text) handler.current(text);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    rec.current = r;
    setListening(true);
    r.start();
  }, [Ctor, locale]);

  const stop = useCallback(() => rec.current?.stop(), []);

  return { supported: Ctor !== null, listening, start, stop };
}
